import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { TopBar } from "@/components/TopBar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Plus, ChevronRight, PawPrint } from "lucide-react";

export default function AdminDashboard() {
  const { t } = useApp();
  const nav = useNavigate();
  const [horses, setHorses] = useState([]);
  const [requests, setRequests] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = () => {
    api.get("/horses").then((r) => setHorses(r.data));
    api.get("/consultations").then((r) => setRequests(r.data));
    api.get("/consultations/unread-count").then((r) => setUnread(r.data.count));
  };
  useEffect(() => { load(); }, []);

  const statusLabel = (s) => ({ new: t("status_new"), reviewed: t("status_reviewed"), closed: t("status_closed") }[s] || s);

  return (
    <div className="min-h-screen">
      <TopBar showLogout />
      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
        <h1 className="font-serif-h text-3xl text-foreground">Admin</h1>

        <Tabs defaultValue="patients" className="mt-8">
          <TabsList className="rounded-full bg-[#EAF0EB] p-1">
            <TabsTrigger value="patients" data-testid="tab-patients" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              {t("patients")}
            </TabsTrigger>
            <TabsTrigger value="requests" data-testid="tab-requests" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              {t("requests")}
              {unread > 0 && (
                <span data-testid="requests-badge" className="ml-2 rounded-full bg-destructive px-2 py-0.5 text-xs text-destructive-foreground">
                  {unread}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="patients" className="mt-6">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {horses.map((h) => (
                <button
                  key={h.id}
                  data-testid={`admin-horse-${h.id}`}
                  onClick={() => nav(`/horse/${h.id}`)}
                  className="group rounded-[12px] border border-border bg-card p-5 text-left shadow-[0_4px_20px_rgba(62,97,72,0.04)] transition-transform hover:-translate-y-1"
                >
                  <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-[#EAF0EB] text-primary">
                    <PawPrint className="h-5 w-5" strokeWidth={1.5} />
                  </div>
                  <div className="font-serif-h text-lg text-foreground">{h.name}</div>
                  <div className="mt-1 text-sm text-muted-foreground">{h.owner_name || h.owner_email || "—"}</div>
                </button>
              ))}
              {horses.length === 0 && <p className="text-muted-foreground">—</p>}
            </div>
          </TabsContent>

          <TabsContent value="requests" className="mt-6">
            <div className="space-y-3">
              {requests.map((r) => (
                <button
                  key={r.id}
                  data-testid={`request-${r.id}`}
                  onClick={() => nav(`/consultation/${r.id}`)}
                  className="flex w-full items-center justify-between gap-4 rounded-[12px] border border-border bg-card p-4 text-left transition-colors hover:bg-accent"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full px-3 py-0.5 text-xs font-medium ${r.type === "riding_lesson" ? "bg-[#EAF0EB] text-primary" : "bg-[#FBF3E7] text-[#8a6d1f]"}`}>
                        {r.type === "riding_lesson" ? t("type_riding") : t("type_consultation")}
                      </span>
                      {r.status === "new" && (
                        <span className="rounded-full bg-destructive px-2 py-0.5 text-xs text-destructive-foreground">{t("new_badge")}</span>
                      )}
                      <span className="text-xs text-muted-foreground">{statusLabel(r.status)}</span>
                    </div>
                    <div className="mt-1 truncate font-medium text-foreground">{r.name}</div>
                    <div className="truncate text-sm text-muted-foreground">{r.horse_name || r.email}</div>
                  </div>
                  <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" strokeWidth={1.5} />
                </button>
              ))}
              {requests.length === 0 && <p className="text-muted-foreground">—</p>}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <button
        data-testid="fab-add-horse"
        onClick={() => nav("/horse-form")}
        className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-[0_8px_30px_rgba(62,97,72,0.3)] transition-transform hover:-translate-y-1"
        title={t("add_horse")}
      >
        <Plus className="h-6 w-6" strokeWidth={2} />
      </button>
    </div>
  );
}
